import { ExportType } from '@components'

import {
  Dropzone,
  Link,
  Modal,
  ModalProps,
  notify,
  SectionMessage,
  Space,
  Text,
  useModal,
  withModalConditionalRender,
} from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useDownloadExportFile } from '@features/exports/hooks/useDownloadExportFile'

import { queryClient } from '../../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'
import { useOrganizationSource } from '../../../../../../hooks/useOrganizationSource'
import { useImportContacts } from './hooks/useImportContacts'
import { useTrackContactsImport } from './hooks/useTrackContactsImport'
import { getFaqLink } from './utils/getFaqLink'

const ACCEPT_CSV = { 'text/csv': ['.csv'], 'text/plain': ['.csv'] }

interface ContactsImportModalProps extends ModalProps {
  descriptionHint?: string
  title?: string
}

const ContactsImportModalComponent = ({
  descriptionHint,
  title,
  ...modalProps
}: ContactsImportModalProps): ReactElement => {
  const { t, i18n } = useTranslation()
  const { close } = useModal(modalProps.id)
  const { isSourceBilly } = useOrganizationSource()
  const faqLink = getFaqLink(i18n.language)
  const { trackImport } = useTrackContactsImport()
  const { downloadExportFile } = useDownloadExportFile()

  const handleImportSuccess = useCallback(
    (data: { created: number; updated: number }) => {
      const count = data.created + data.updated
      trackImport({ phase: 'Completed', contactCount: count })
      queryClient.invalidateQueries(QueryKeys.ContactsAndContactPersons)
      close()
      notify({
        id: NotificationKeys.ContactsImport,
        variant: 'success',
        message: (
          <Trans i18nKey="contacts.import.success_notification" values={{ count }} components={{ 1: <strong /> }} />
        ),
      })
    },
    [close, trackImport],
  )

  const handleImportError = useCallback(
    (error: Error) => {
      trackImport({ phase: 'Failed', errorReason: error.message || 'unknown' })
      close()
      notify({
        id: NotificationKeys.ContactsImport,
        variant: 'error',
        message: t('contacts.import.error'),
      })
    },
    [close, t, trackImport],
  )

  const { importContacts, isImporting, reset } = useImportContacts({
    onSuccess: handleImportSuccess,
    onError: handleImportError,
  })

  const handleDropAccepted = useCallback(
    ([file]: File[]) => {
      trackImport({ phase: 'Started' })
      reset()
      importContacts(file)
    },
    [importContacts, reset, trackImport],
  )

  const handleDownloadExampleClick = useCallback(() => {
    downloadExportFile({ exportUrl: 'contacts', type: ExportType.Csv })
  }, [downloadExportFile])

  return (
    <Modal id={modalProps.id}>
      <Modal.Header title={title ?? t('contacts.import.modal_title')} />
      <Modal.Body>
        <Text colorVariant="secondary">
          <Trans
            i18nKey="contacts.import.description"
            components={{
              1: <Link onClick={handleDownloadExampleClick} withAnimation />,
              2: <strong />,
            }}
          />
        </Text>
        <Text colorVariant="secondary">{descriptionHint ?? <Trans i18nKey="contacts.import.description_hint" />}</Text>
        {!isSourceBilly && (
          <>
            <Space size="s16" />
            <SectionMessage variant="info">
              <Trans
                i18nKey="contacts.import.faq_link"
                components={{
                  1: <Link href={faqLink} target="_blank" rel="noopener noreferrer" withAnimation />,
                }}
              />
            </SectionMessage>
          </>
        )}

        <Space size="s16" />

        <Dropzone
          accept={ACCEPT_CSV}
          contentText={t('contacts.import.dropzone_content')}
          footerText={t('contacts.import.dropzone_footer')}
          isUploading={isImporting}
          maxFiles={1}
          onDropAccepted={handleDropAccepted}
          uploadingText={t('contacts.import.uploading')}
        />
      </Modal.Body>
    </Modal>
  )
}

export const ContactsImportModal = withModalConditionalRender(ContactsImportModalComponent)
